import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, X, Loader2, FileText, Mail, Check, Shield } from 'lucide-react';
import { toPng } from 'html-to-image';
import { liteApi } from '@/utils/lite/liteApi';

/**
 * ActivityShareImage — generates a 1080×1350 parent-handout PNG from a
 * generated activity. Designed to be sent to caregivers via WhatsApp/iMessage
 * /email so they can practice between sessions. Includes materials and
 * step-by-step instructions, language to model, and follow-the-lead tip.
 *
 * No PHI/clinic header is baked into the image. Render is fully browser-side.
 */

// Soft, parent-friendly themes keyed off play_type.
const THEMES = {
  sensory: { bg: '#FFF7F4', accent: '#A86B5C', soft: '#FBE8DD', ink: '#2A1C16' },
  movement: { bg: '#F0FAFB', accent: '#0E7C86', soft: '#CFEEF1', ink: '#0B3A40' },
  pretend: { bg: '#FFFAF2', accent: '#B45309', soft: '#FCE6C8', ink: '#3D2A06' },
  music: { bg: '#FAF5FF', accent: '#7C3AED', soft: '#EADBFB', ink: '#2E1065' },
  art: { bg: '#FFF5F6', accent: '#BE123C', soft: '#FBD3D9', ink: '#4C0519' },
  construction: { bg: '#F2FBF6', accent: '#047857', soft: '#CFEFDD', ink: '#062E20' },
  outdoor: { bg: '#FAFCEC', accent: '#65A30D', soft: '#E5F1C5', ink: '#1A2E05' },
  default: { bg: '#FAF7F2', accent: '#9A3412', soft: '#FCE5D2', ink: '#1C1917' },
};

function pickTheme(playType = '') {
  const key = (playType || '').toLowerCase();
  for (const k of Object.keys(THEMES)) {
    if (k !== 'default' && key.includes(k)) return THEMES[k];
  }
  return THEMES.default;
}

const HandoutCard = forwardRef(function HandoutCard(
  { theme, activity, materialsFallback, code },
  ref,
) {
  const materials = (activity?.materials_list && activity.materials_list.length > 0)
    ? activity.materials_list
    : (materialsFallback ? materialsFallback.split(',').map((s) => s.trim()).filter(Boolean) : []);
  const steps = (activity?.steps || []).slice(0, 5);
  const phrases = (activity?.language_examples || []).slice(0, 4);

  return (
    <div
      ref={ref}
      data-testid="handout-card-1080"
      style={{
        width: 1080,
        height: 1350,
        background: theme.bg,
        boxSizing: 'border-box',
        padding: '64px 72px',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Fraunces", "Georgia", serif',
        color: theme.ink,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent strip */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 12, background: theme.accent }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div
          style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: 14,
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: theme.accent,
            fontWeight: 700,
          }}
        >
          A play activity for you to try
        </div>
        <div
          style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: 13,
            color: theme.ink,
            opacity: 0.55,
            fontWeight: 500,
          }}
        >
          {activity?.play_type || 'Play'}
        </div>
      </div>

      {/* Title */}
      <h1
        style={{
          margin: 0,
          fontSize: 76,
          lineHeight: 1.05,
          letterSpacing: -1.5,
          fontWeight: 600,
          maxWidth: '95%',
        }}
      >
        {activity?.name || 'Activity'}
      </h1>

      {/* Materials */}
      {materials.length > 0 && (
        <Section theme={theme} title="What you'll need" marginTop={36}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {materials.slice(0, 6).map((m, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: 18,
                  padding: '8px 16px',
                  borderRadius: 10,
                  background: theme.soft,
                  color: theme.ink,
                  fontWeight: 500,
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Steps */}
      {steps.length > 0 && (
        <Section theme={theme} title="What to do" marginTop={28}>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {steps.map((s, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: 22,
                  lineHeight: 1.4,
                  color: theme.ink,
                }}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: theme.accent,
                    color: 'white',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 18,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ flex: 1 }}>{s}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Language to try */}
      {phrases.length > 0 && (
        <Section theme={theme} title="Try saying" marginTop={28}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {phrases.map((p, i) => (
              <div
                key={i}
                style={{
                  fontSize: 24,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  paddingLeft: 18,
                  borderLeft: `4px solid ${theme.accent}`,
                  color: theme.ink,
                }}
              >
                “{p}”
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Follow lead tip */}
      {activity?.follow_lead_guidance && (
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 28,
            display: 'flex',
            gap: 16,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 6,
              alignSelf: 'stretch',
              borderRadius: 3,
              background: theme.accent,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: 13,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: theme.accent,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Follow their lead
            </div>
            <div
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: 18,
                lineHeight: 1.5,
                color: theme.ink,
                opacity: 0.85,
              }}
            >
              {activity.follow_lead_guidance}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: `1px solid ${theme.accent}30`,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 13,
          color: theme.ink,
          opacity: 0.6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>Shared by your speech-language therapist · Adapt to what works for your child.</span>
        {code && (
          <span
            style={{
              fontFamily: '"SF Mono", "Menlo", "Consolas", monospace',
              fontSize: 12,
              letterSpacing: 1.5,
              padding: '4px 10px',
              borderRadius: 6,
              background: theme.soft,
              color: theme.ink,
              opacity: 1,
              flexShrink: 0,
            }}
          >
            {code}
          </span>
        )}
      </div>
    </div>
  );
});

function Section({ theme, title, children, marginTop = 24 }) {
  return (
    <div style={{ marginTop }}>
      <div
        style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 13,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: theme.accent,
          fontWeight: 700,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// 8-char human-readable code: A-Z + 2-9 (no 0/O/1/I), grouped XXXX-XXXX
function genCode() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 8; i += 1) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `${s.slice(0, 4)}-${s.slice(4)}`;
}

export default function ActivityShareImage({ activity, materialsFallback, onClose }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const code = useMemo(() => genCode(), []);
  const theme = pickTheme(activity?.play_type);

  // Tracking-email form state
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackEmail, setTrackEmail] = useState(() => {
    try { return localStorage.getItem('playfirst_lite_email') || ''; } catch { return ''; }
  });
  const [trackLabel, setTrackLabel] = useState('');
  const [tracking, setTracking] = useState(false);
  const [trackSent, setTrackSent] = useState(false);
  const [trackError, setTrackError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setError('');
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        width: 1080,
        height: 1350,
        backgroundColor: theme.bg,
      });
      const a = document.createElement('a');
      const safeName = (activity?.name || 'activity')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
      a.href = dataUrl;
      a.download = `${safeName || 'activity'}-handout.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
      setError('Image export failed. Try Copy instead, or refresh and retry.');
    } finally {
      setDownloading(false);
    }
  };

  const handleTrack = async () => {
    setTrackError('');
    if (!trackEmail || !trackEmail.includes('@')) {
      setTrackError('Enter a valid email so we can send you the tracking record.');
      return;
    }
    setTracking(true);
    try {
      const stepsSummary = (activity?.steps || []).slice(0, 3).map((s, i) => `${i + 1}. ${s}`).join(' ');
      await liteApi.trackHandout({
        email: trackEmail,
        code,
        activity_name: activity?.name || 'Therapy Activity',
        activity_summary: stepsSummary,
        sent_to_label: (trackLabel || '').trim().slice(0, 60),
      });
      try { localStorage.setItem('playfirst_lite_email', trackEmail); } catch {/* ignore */}
      setTrackSent(true);
    } catch (e) {
      setTrackError(e.message || 'Could not send tracking email.');
    } finally {
      setTracking(false);
    }
  };

  // 1080×1350 scaled to fit ~440×550 modal preview
  const scale = 0.4074; // 1080 → 440
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="activity-share-modal"
      onClick={() => !downloading && onClose?.()}
    >
      <div
        className="bg-white rounded-3xl max-w-[560px] w-full overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary-700" />
            <h3 className="font-serif text-lg text-primary-900">Parent handout</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="share-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Visible scaled preview — wrapper sized to scaled dimensions */}
          <div className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-50 mx-auto" style={{ width: Math.round(1080 * scale), height: Math.round(1350 * scale) }}>
            <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
              <HandoutCard
                ref={cardRef}
                theme={theme}
                activity={activity}
                materialsFallback={materialsFallback}
                code={code}
              />
            </div>
          </div>

          <p className="text-xs text-stone-500 leading-relaxed">
            1080×1350 PNG · designed to text/email/AirDrop to a caregiver. No clinic info or PHI is included by default.
          </p>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="share-error">
              {error}
            </div>
          )}

          {/* SLP tracking section */}
          <div className="rounded-xl border border-stone-200 bg-stone-50/60">
            {!trackOpen ? (
              <button
                type="button"
                onClick={() => setTrackOpen(true)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-stone-100/50 rounded-xl"
                data-testid="track-open-btn"
              >
                <div className="flex items-center gap-2 text-stone-700">
                  <Mail className="w-4 h-4" />
                  <span><span className="font-medium">Email me a record</span> when I send this to a parent</span>
                </div>
                <span className="text-stone-400 text-xs">code <span className="font-mono text-stone-700">{code}</span> →</span>
              </button>
            ) : trackSent ? (
              <div className="p-4 flex items-center gap-2 text-sm text-emerald-800" data-testid="track-sent-confirmation">
                <Check className="w-4 h-4" /> Tracking record sent to <span className="font-medium">{trackEmail}</span>. Search your inbox for code <span className="font-mono">{code}</span>.
              </div>
            ) : (
              <div className="p-4 space-y-3" data-testid="track-form">
                <div className="flex items-start gap-2 text-xs text-stone-600">
                  <Shield className="w-3.5 h-3.5 mt-0.5 text-emerald-700 flex-shrink-0" />
                  <span>
                    Stateless — we email you the code, then forget it. The PNG already has{' '}
                    <span className="font-mono">{code}</span> on it. Use a <span className="italic">non-identifying</span> label
                    (e.g. "Tuesday 2pm" — never a child's name).
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Your email</Label>
                    <Input
                      type="email" placeholder="you@clinic.com"
                      value={trackEmail}
                      onChange={(e) => setTrackEmail(e.target.value)}
                      data-testid="track-email-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Label (optional)</Label>
                    <Input
                      placeholder="e.g. Tuesday 2pm"
                      value={trackLabel}
                      onChange={(e) => setTrackLabel(e.target.value)}
                      data-testid="track-label-input"
                      maxLength={60}
                    />
                  </div>
                </div>
                {trackError && <p className="text-xs text-red-600">{trackError}</p>}
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setTrackOpen(false)} className="rounded-full" disabled={tracking} data-testid="track-cancel-btn">
                    Skip
                  </Button>
                  <Button size="sm" onClick={handleTrack} disabled={tracking} className="rounded-full" data-testid="track-send-btn">
                    {tracking ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</> : <><Mail className="w-3.5 h-3.5 mr-1.5" /> Send record</>}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="share-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={downloading} className="rounded-full" data-testid="share-download-btn">
              {downloading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rendering…</>
                : <><Download className="w-4 h-4 mr-2" /> Download PNG</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
